import React from 'react';
import i18n from '../../../../i18n';
import { getOrganizationList } from '../../../../models/organization';
import { getGroupsList } from '../../../../models/groups';
import { SideBar } from '../../sidebar';
import Dropdown from '../../../common/DropDown';
import NoData from '../../../common/NoData';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import FilterSection from '../../../common/Filter';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import DataTable from 'react-data-table-component';
import { getLoggedUser } from '../../../../state/user';
import { getWalletGroups } from './services/WalletTransactionServices';
import { ActionModal } from './components/ActionModal';
import { NavLink } from 'react-router-dom';
import { ClientAmountCard } from '../../../common/ClientAmountCard';
import { ArrowLeft, ArrowRight, Download, FileSpreadsheet } from 'lucide-react';
import { withRouter } from '../../../../hoc/withRouter';
import HeaderBanner from '../../../common/HeaderBanner';
import NewDropdown from '../../../common/NewDropDown';
import { downloadFile } from '../../../../utils/downloadFile';
import { apiUrl } from '../../../../constants/global';
import { TEST_WALLET_STATUS } from '../../../../constants/status';

const user = getLoggedUser();

const roleAllowedStatuses = {
  PM: ['pending', 'completed'],
  F: ['pending', 'completed', 'rejected'],
  CE: ['pending', 'completed'],
  GM: ['pending', 'completed'],
  C: ['pending', 'completed', 'rejected'],
};
const allStatuses = [
  { type: 'pending', title: 'Pending', amount: 0, auctionCount: 0 },
  { type: 'fs_approved', title: 'FS Approved', amount: 0, auctionCount: 0 },
  { type: 'completed', title: 'Completed', amount: 0, auctionCount: 0 },
  { type: 'rejected', title: 'Rejected', amount: 0, auctionCount: 0 },
];
const allowedStatuses = user ? roleAllowedStatuses[user.role] || [] : [];
const filteredMeta = allStatuses.filter((card) => allowedStatuses.includes(card.type));

const allowedSupplierPaymentRoles = ['F', 'PM'];
const allowedBackRoles = ['CE', 'GM'];

class AuctionPayment extends React.Component {
  constructor() {
    super();
    this.state = {
      search: {
        startDate: null,
        endDate: null,
        account: '1',
        client: '',
        group: '',
        bidder: '',
      },
      status: filteredMeta[0]?.type || 'rejected', // default to first allowed
      activeTab: filteredMeta[0]?.type || 'rejected',
      cardMeta: filteredMeta,
      input: {},
      clients: [],
      groups: [],
      customers: [],
      auctionPayments: [],
      isLoading: false,
      limit: 15,
      page: 1,
      columns: [
        {
          id: 1,
          name: 'Sl No',
          width: '70px',
          sortable: false,
          center: true,
          cell: (row, i) => i + 1,
        },
        {
          id: 2,
          name: 'ID',
          width: '80px',
          sortable: true,
          center: true,
          cell: (row) => row.id,
        },
        {
          id: 3,
          name: i18n.t('project_code'),
          minWidth: '120px',
          cell: (row) => row.project_code,
        },
        {
          id: 4,
          name: i18n.t('client'),
          minWidth: '140px',
          cell: (row) => row.organization_name,
          wrap: true,
        },
        {
          id: 5,
          name: i18n.t('transactions'),
          minWidth: '140px',
          cell: (row) => row.transactions_count,
          wrap: true,
        },
        {
          id: 6,
          name: i18n.t('start_date'),
          minWidth: '180px',
          cell: (row) => row.start_date,
          wrap: true,
        },
        {
          id: 7,
          name: i18n.t('end_date'),
          width: '110px',
          center: true,
          cell: (row) => row.end_date,
        },
        ...(user.role === 'F' || user.role === 'PM'
          ? [
              {
                id: 20,
                name: i18n.t('action'),
                minWidth: '160px',
                center: true,
                cell: (row) => (
                  <div className="action-buttons">
                    <ActionButton key="view" label="view" to={this.buildDetailsLink(row)} />
                  </div>
                ),
              },
            ]
          : []),
      ],
      showConfirm: false,
      selectedRow: null,
      confirmAction: null,
    };

    this.handleSearchChange = this.handleSearchChange.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    // Parse URL query params
    const params = new URLSearchParams(window.location.search);
    const search = {
      startDate: params.get('startDate') || null,
      endDate: params.get('endDate') || null,
      account: params.get('account') || '1',
      client: params.get('client') || '',
      group: params.get('group') || '',
      bidder: params.get('bidder') || '',
    };
    const status = params.get('status') || 'pending';
    const page = parseInt(params.get('page')) || 1;
    const limit = parseInt(params.get('limit')) || 15;

    this.setState({ search, status, page, limit, activeTab: status }, () => {
      this.getAllOrganization();
      // Arriving with a client already selected — load its groups so the
      // dropdown isn't empty
      if (search.client) this.getAllGroups();
      this.getAuctionPayments();
    });
  }

  // Update URL without refreshing page
  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null))
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  // Shared filter context, used by the back button and the row details link
  buildFilterQuery = (extra = {}) => {
    const { startDate, endDate, account, client, group, bidder } = this.state.search;
    return new URLSearchParams(
      Object.fromEntries(
        Object.entries({
          startDate,
          endDate,
          account,
          client,
          group,
          bidder,
          ...extra,
        }).filter(([_, v]) => v !== null && v !== '' && v !== undefined)
      )
    ).toString();
  };

  buildDetailsLink = (row) =>
    `/auction-payment/${row.id}?${this.buildFilterQuery({ status: this.state.status })}`;

  async getAuctionPayments() {
    this.setState({ isLoading: true });
    let { limit, page, status } = this.state;
    let { startDate, endDate, group, client, account, bidder } = this.state.search;
    if (!status) {
      this.setState({ isLoading: false });
      return;
    }
    const q = {
      page: page,
      limit: limit,
    };
    if (startDate) q.startDate = startDate;
    if (endDate) q.endDate = endDate;
    if (account) q.account = account;
    if (group) q.group = group;
    if (client) q.client = client;
    if (bidder) q.bidder = bidder;
    if (status) q.status = status;

    // Update URL
    this.updateUrlQuery(q);
    try {
      let response = await getWalletGroups({ ...q, type: 'client_payment' });
      if (response.success) {
        let auctionPayments = response.data;
        let { total, pending, approved, completed, rejected } = response.meta;
        this.setState({
          auctionPayments,
          total,
          cardMeta: [
            {
              type: 'pending',
              title: 'Pending',
              amount: pending.total,
              auctionCount: pending.count,
            },
            {
              type: 'fs_approved',
              title: 'FS Approved',
              amount: approved.total,
              auctionCount: approved.count,
            },
            {
              type: 'completed',
              title: 'Completed',
              amount: completed.total,
              auctionCount: completed.count,
            },
            {
              type: 'rejected',
              title: 'Rejected',
              amount: rejected.total,
              auctionCount: rejected.count,
            },
          ].filter((card) => allowedStatuses.includes(card.type)),
        });
      }
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async getAllOrganization() {
    const { account, startDate, endDate } = this.state.search;
    const { status } = this.state;

    const q = { hasWalletTransactions: true };
    if (account) q.account = account;
    if (status) q.walletStatus = this.statusToWalletCode(status);
    if (startDate) q.startDate = startDate;
    if (endDate) q.endDate = endDate;

    const response = await getOrganizationList(q);
    let clients = response.data;
    this.setState({ clients });
  }

  statusToWalletCode = (status) => {
    switch (status) {
      case 'pending':
        return 'B';
      case 'fs_approved':
        return 'FS';
      case 'completed':
        return 'FA';
      default:
        return null;
    }
  };

  async getAllGroups() {
    this.setState({ groupLoading: true });
    let { client } = this.state.search;
    let q = client ? `organization=${client}` : '';
    const response = await getGroupsList(q);
    if (response.success) {
      let groups = response.data;
      this.setState({ groups, groupLoading: false });
    } else {
      this.setState({ groupLoading: false });
    }
  }

  handleSearchChange = (e) => {
    const { name, value } = e.target;

    this.setState(
      (prevState) => {
        const updatedSearch = { ...prevState.search, [name]: value };
        // Changing the client invalidates the current group selection
        if (name === 'client') updatedSearch.group = '';
        return { search: updatedSearch };
      },
      () => {
        if (name === 'client') {
          this.getAllGroups();
        }
      }
    );
  };

  handleSearch = () => {
    this.setState({ page: 1 }, () => {
      this.getAllOrganization();
      this.getAuctionPayments();
    });
  };

  handleClear = () => {
    // account and bidder are page context carried in from the cash flow
    // dashboard, not user-set filters — keep them
    this.setState(
      (prevState) => ({
        search: {
          startDate: null,
          endDate: null,
          account: prevState.search.account,
          client: '',
          group: '',
          bidder: prevState.search.bidder,
        },
        groups: [],
        page: 1,
      }),
      () => {
        this.getAuctionPayments();
      }
    );
  };

  onChangePage = (page) => {
    this.setState({ page: page }, () => {
      this.getAuctionPayments();
    });
  };

  onChangeRowsPerPage = (limit) => {
    this.setState({ limit: limit, page: 1 }, () => {
      this.getAuctionPayments();
    });
  };

  handleCardSelect = (cardType) => {
    this.setState(
      {
        activeTab: cardType,
        status: cardType,
        page: 1,
      },
      () => {
        this.getAllOrganization();
        this.getAuctionPayments();
      }
    );
  };

  handleActions = (row) => {
    this.setState({
      showConfirm: true,
      selectedRow: row,
    });
  };

  handleDownload = async (fileType) => {
    const { account, client, group } = this.state.search;

    // Only send filters that are set; the backend treats missing keys as "no filter"
    const params = Object.fromEntries(
      Object.entries({
        status: TEST_WALLET_STATUS[this.state.status],
        type: 'client_payment',
        fileType,
        accountType: account,
        clientId: client,
        groupId: group,
      }).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );

    this.setState({ exportLoading: true });
    try {
      await downloadFile({
        url: `${apiUrl}v2/report/wallet`,
        params,
        token: user.token,
        fileType,
        fileName: 'Auction_Transaction',
      });
    } finally {
      this.setState({ exportLoading: false });
    }
  };

  handleDownloadAction = async (fileType) => {
    const { startDate, endDate, account, client, group } = this.state.search;

    // Only send filters that are set; the backend treats missing keys as "no filter"
    const params = Object.fromEntries(
      Object.entries({
        status: TEST_WALLET_STATUS[this.state.status],
        type: 'client_payment',
        fileType,
        startDate,
        endDate,
        account,
        client,
        group,
      }).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );

    this.setState({ exportLoading: true });
    try {
      await downloadFile({
        url: `${apiUrl}v2/wallet-groups/export`,
        params,
        token: user.token,
        fileType,
        fileName: 'Auction_Transaction',
      });
    } finally {
      this.setState({ exportLoading: false });
    }
  };

  // Shared list content (cards + filters + table) used by both the
  // client (cd-page) layout and the internal (sidebar) layout, mirroring
  // EnrollmentListNew's renderListContent() pattern.
  renderListContent() {
    const isClientRole = user?.role === 'C';
    const { clients, groups, groupLoading } = this.state;

    return (
      <>
        <div className="client-cards-container-new">
          {this.state.cardMeta.map((config) => (
            <ClientAmountCard
              key={config.type}
              {...config}
              selected={this.state.activeTab === config.type}
              onSelect={this.handleCardSelect}
            />
          ))}
        </div>
        <FilterSection
          loading={this.state.isLoading}
          onSearch={this.handleSearch}
          onClear={this.handleClear}
        >
          {/* Start Date */}
          <div className="select-wrapper floating-label-wrapper">
            <input
              id="startDate"
              type="date"
              name="startDate"
              value={this.state.search.startDate || ''}
              onChange={this.handleSearchChange}
              className="filter-base floating-input"
              placeholder=" " // Important: single space for floating effect
            />
            <label htmlFor="startDate" className="floating-label">
              {i18n.t('start_date')}
            </label>
          </div>

          {/* End Date */}
          <div className="select-wrapper floating-label-wrapper">
            <input
              id="endDate"
              type="date"
              name="endDate"
              value={this.state.search.endDate || ''}
              onChange={this.handleSearchChange}
              className="filter-base floating-input"
              placeholder={i18n.t('end_date')}
            />
            <label htmlFor="endDate" className="floating-label">
              {i18n.t('end_date')}
            </label>
          </div>

          {/* Account Type — hidden for client role */}
          {!isClientRole && (
            <Dropdown
              name="account"
              value={this.state.search.account}
              onChange={this.handleSearchChange}
              placeholder={i18n.t('account_type')}
              empty={i18n.t('select_account')}
              options={[
                { value: '1', label: i18n.t('mzadcom_account') },
                { value: '2', label: i18n.t('client_account') },
              ]}
            />
          )}

          {/* Client Dropdown - hidden for client role (they only see their own data) */}
          {!isClientRole && (
            <Dropdown
              name="client"
              value={this.state.search.client}
              onChange={this.handleSearchChange}
              placeholder={i18n.t('client_name')}
              empty={i18n.t('select_client')}
              options={clients.map((c) => ({ value: c.id, label: c.organization_name }))}
            />
          )}

          {/* Group Dropdown */}
          <Dropdown
            name="group"
            value={this.state.search.group}
            onChange={this.handleSearchChange}
            placeholder={i18n.t('auction_group')}
            empty={groupLoading ? `${i18n.t('loading')}...` : i18n.t('select_auction')}
            options={groups.map((g) => ({ value: g.id, label: g.group_name }))}
            disabled={groupLoading}
          />
        </FilterSection>

        <div className="Data-Listing">
          {this.state.isLoading ? (
            <TableSkeleton columns={6} rows={10} />
          ) : this.state.auctionPayments.length ? (
            <DataTable
              responsive={true}
              columns={this.state.columns}
              data={this.state.auctionPayments}
              customStyles={tableStyles}
              highlightOnHover
              pagination
              paginationServer
              paginationTotalRows={this.state.total}
              paginationPerPage={this.state.limit}
              paginationDefaultPage={this.state.page}
              paginationRowsPerPageOptions={[15, 25, 50, 100]}
              paginationComponentOptions={{
                rowsPerPageText: i18n.t('rows_per_page'),
                rangeSeparatorText: i18n.t('out'),
              }}
              onChangePage={this.onChangePage}
              onChangeRowsPerPage={this.onChangeRowsPerPage}
            />
          ) : (
            <NoData
              message="No Auction Payments available"
              actionText={i18n.t('refresh')}
              onAction={this.getAuctionPayments.bind(this)}
            />
          )}
        </div>
      </>
    );
  }

  renderExportAction() {
    const { exportLoading } = this.state;

    return (
      <NewDropdown
        label={i18n.t('export')}
        loading={exportLoading}
        icon={Download}
        items={[
          {
            label: i18n.t('excel'),
            icon: FileSpreadsheet,
            onClick: () => this.handleDownloadAction('excel'),
          },
          {
            label: i18n.t('excel_auctions'),
            icon: FileSpreadsheet,
            onClick: () => this.handleDownload('excel'),
          },
        ]}
      />
    );
  }

  render() {
    const { showConfirm, selectedRow } = this.state;
    const isClientRole = user?.role === 'C';

    const modal = (
      <ActionModal
        show={showConfirm}
        selectedPayment={selectedRow}
        onClose={() => this.setState({ showConfirm: false })}
        onComplete={() => {
          this.getAuctionPayments();
          this.setState({ showConfirm: false, selectedRow: null });
        }}
      />
    );

    if (isClientRole) {
      return (
        <React.Fragment>
          <div className="cd-page">
            <div className="cd-shell">
              <p className="cd-crumb">
                <NavLink to="/client-management">{i18n.t('dashboard')}</NavLink>
                <span className="cd-crumb__current">
                  {i18n.t('auction_payments') || 'Auction Payments'}
                </span>
              </p>

              <header className="cd-hero cd-hero--compact">
                <h1 className="cd-hero__title">{i18n.t('auction_payments')}</h1>
                <p className="cd-hero__sub">{i18n.t('track_your_successful_bid')}</p>
              </header>

              <div className="card">
                <div className="card-body">{this.renderListContent()}</div>
              </div>
            </div>
          </div>
          {modal}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('auction_payments')}
          // subtitle={i18n.t('track_your_successful_bid')}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!this.state.hideSidebar && <SideBar />}
              </div>
              <div
                className={
                  this.state.hideSidebar
                    ? 'col-xl-12 col-lg-12 col-md-12'
                    : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => {
                          this.setState({
                            hideSidebar: !this.state.hideSidebar,
                          });
                        }}
                      >
                        {this.state.hideSidebar && <i className="icon-size-actual"></i>}
                        {!this.state.hideSidebar && <i className="icon-size-fullscreen"></i>}
                      </span>
                    </div>
                    <div className="header-action">
                      {allowedSupplierPaymentRoles.includes(user?.role) && (
                        <button
                          className="button1 button1-gradient button1-forward"
                          onClick={() => this.props.navigate('/auction_approval')}
                        >
                          <span>Supplier Payment</span>
                          <ArrowRight size={18} className="icon" />
                        </button>
                      )}
                      {allowedBackRoles.includes(user?.role) && (
                        <NavLink
                          to={{
                            pathname: '/cash-flow',
                            search: this.buildFilterQuery(),
                          }}
                          className="button1 button1-gradient button1-back"
                        >
                          <ArrowLeft size={18} className="icon" />
                          <span>{i18n.t('back')}</span>
                        </NavLink>
                      )}

                      {this.renderExportAction()}
                    </div>
                  </div>
                  <div className="card-body">{this.renderListContent()}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {modal}
      </React.Fragment>
    );
  }
}

const ActionButton = ({ label, icon, to, variant = 'button1-primary', onClick }) => {
  if (to) {
    return (
      <NavLink to={to} className={`button1 ${variant} button1-action`}>
        {icon} {i18n.t(label)}
      </NavLink>
    );
  }

  return (
    <button className={`button1 ${variant} button1-action`} onClick={onClick}>
      {icon} {i18n.t(label)}
    </button>
  );
};

export default withRouter(AuctionPayment);
